import { FusionError } from '@fusion-advantage/backcompat'
import { SyncItemStatus, WorkItemKey } from "@fusion-advantage/formlib"
import { OfflineTableId } from "../../dal/OfflineTable"
import { Result } from "neverthrow"

export enum SyncItemType {
	// cloudToDevice
	AppStatus = 'appStatus',
	FrontendUpdate = 'frontendUpdate',
	EtagTest = 'etagTest',
	OfflineTable = 'offlineTable',
	Todo = 'todo',
	Appt = 'appt',
	ReplaceApptEncounters = 'replaceApptEncounters',
	Export = 'export',
	Branch = 'branch',
	Renderer = 'renderer',
	CloudToDeviceFile = 'cloudToDeviceFile',
	WriteCloudToDevice = 'writeCloudToDevice',

	// deviceToCloud
	BatchDeviceToCloud = 'batchDeviceToCloud',
	EncounterStatus = 'encounterStatus',
	Import = 'import',
	DeviceToCloudFile = 'deviceToCloudFile',
	ImportProcessingAllowance = 'importProcessingAllowance',
}

export type SyncItemKey = {
	type: SyncItemType.AppStatus
} | {
	type: SyncItemType.FrontendUpdate
} | {
	type: SyncItemType.EtagTest
} | {
	type: SyncItemType.OfflineTable
	tableId: OfflineTableId
} | {
	type: SyncItemType.Todo
} | {
	type: SyncItemType.Appt
} | {
	type: SyncItemType.ReplaceApptEncounters
} | {
	type: SyncItemType.Export
	workItemKey: WorkItemKey
	sfApptNum?: string
} | {
	type: SyncItemType.Branch
	branchGuid: string
} | {
	type: SyncItemType.Renderer
	rendererNumber: number
} | {
	type: SyncItemType.CloudToDeviceFile
	fileId: string
} | {
	type: SyncItemType.WriteCloudToDevice
} | {
	type: SyncItemType.BatchDeviceToCloud
} | {
	type: (
		SyncItemType.EncounterStatus|
		SyncItemType.Import
	)
	encounterGuid: string
	ordinal: number
} | {
	type: SyncItemType.DeviceToCloudFile
	fileId: string
} | {
	type: SyncItemType.ImportProcessingAllowance
}

export const syncItemIsCloudToDevice = (key: SyncItemKey): boolean => {
	switch(key.type) {
		case SyncItemType.AppStatus:
		case SyncItemType.FrontendUpdate:
		case SyncItemType.EtagTest:
		case SyncItemType.OfflineTable:
		case SyncItemType.Todo:
		case SyncItemType.Appt:
		case SyncItemType.ReplaceApptEncounters:
		case SyncItemType.Export:
		case SyncItemType.Branch:
		case SyncItemType.Renderer:
		case SyncItemType.CloudToDeviceFile:
		case SyncItemType.WriteCloudToDevice:
			return true
		default:
			return false
	}
}

export const syncItemIsDeviceToCloud = (key: SyncItemKey): boolean => {
	switch(key.type) {
		case SyncItemType.BatchDeviceToCloud:
		case SyncItemType.EncounterStatus:
		case SyncItemType.Import:
		case SyncItemType.DeviceToCloudFile:
		case SyncItemType.ImportProcessingAllowance:
			return true
		default:
			return false
	}
}

export type SyncItemResultOk<T> = {
	status: (
		SyncItemStatus.Ok|
		SyncItemStatus.DownloadOk|
		SyncItemStatus.UploadOk|
		SyncItemStatus.NotModified
	)
	value: T
}

export type SyncItemResultErr = {
	status: (
		SyncItemStatus.NoAuth|
		SyncItemStatus.NoConnection|
		SyncItemStatus.UpdateRequired|
		SyncItemStatus.Canceled|
		SyncItemStatus.Pending
	)
	error?: never
} | {
	status: (
		SyncItemStatus.InvalidBody|
		SyncItemStatus.Unknown
	)
	error: FusionError
}

export type SyncItemResult<T> = Result<SyncItemResultOk<T>, SyncItemResultErr>

export type SyncItemDeferredWriteTask = () => Promise<Result<void, SyncItemResultErr>>

export type SyncItemDeferredWriteResultOk<T> = SyncItemResultOk<T> & {
	deferredWriteTask?: SyncItemDeferredWriteTask
}

export type SyncItemDeferredWriteResult<T> = Result<SyncItemDeferredWriteResultOk<T>, SyncItemResultErr>

// without the data, so it can be persisted to redux
export type SyncItemStateResult = {
	status: (
		SyncItemStatus.Ok|
		SyncItemStatus.DownloadOk|
		SyncItemStatus.UploadOk|
		SyncItemStatus.NotModified
	)
} | SyncItemResultErr  // ok to persist to redux as-is

export const syncItemResultToStateResult = <T>(r: SyncItemResult<T>): SyncItemStateResult => {
	if(r.isOk()) {
		return {
			status: r.value.status,
		}
	}
	return r.error
}

export enum SyncItemSubtaskType {
	Start = 'start',
	LdbRead1 = 'ldbRead1',
	ApiRequest1 = 'apiRequest1',
	ApiRequest2 = 'apiRequest2',
	Parse = 'parse',
	LdbWrite1 = 'ldbWrite1',
	LdbWrite2 = 'ldbWrite2',
	End = 'end',
	DerivativeTaskPrep = 'derivativeTaskPrep'
}
